import { Button, ButtonVariants, MultiSelect as DSMultiSelect, MultiSelectProps as DSMultiSelectProps, TMultiSelectItem } from "@centricsoftware/design-system";
import { CLEAR_RES, GO_TO_DIALOG_RES, INVALID_RES, OPEN_RES } from "csi/components/constants";
import { StoreNames } from "csi/store/Names";
import { useLocalStoreWithDep } from "designSystem/hooks";
import { observer } from "mobx-react-lite";
import React, { FC } from "react";
import { useIsMounted } from "../hooks";
import { MultiSelectStore } from "./store/MultiSelectStore";
import { GoToDialogSave, OnLoadOptions } from "./store/SelectStoreBase";

export interface MultiSelectProps
	extends Omit<
		DSMultiSelectProps<TMultiSelectItem>,
		"clearButtonAriaLabel" |
		"dropdownButtonAriaLabel" |
		"invalidListItemLabel" |
		"noOptionsLabel" |
		"value"
	> {
	/** store names which includes the aggregate store */
	aggregateStore: StoreNames;

	/** on options callback */
	onLoadOptions?: OnLoadOptions;

	/** callback when go to dialog save button is clicked */
	goToDialogSave?: GoToDialogSave;

	/** using a string value because DS supports this and simplifies types */
	value?: string[];
}

const MultiSelect: FC<MultiSelectProps> = observer(({
	aggregateStore,
	error,
	errorMessage,
	goToDialogSave,
	label,
	onChange,
	onClose,
	onLoadOptions,
	options: displayOptions,
	value,
	...otherProps
}) => {
	const isMounted = useIsMounted();
	const multiSelectStore = useLocalStoreWithDep(
		() => new MultiSelectStore(aggregateStore, onLoadOptions, label, goToDialogSave, displayOptions, value),
		[aggregateStore, displayOptions, value]);

	if (!multiSelectStore) {
		return;
	}

	const { canGoToDialog, errorText, isLoading, onListActionClick, onOpen, options, updateMultiSelectValue } = multiSelectStore;
	const isError = error || !!errorMessage || !!errorText;

	// call onClose when opening goToDialog
	const callListActionAndCloseDropdown = (): void => {
		onClose?.();
		onListActionClick();
	};

	// update the multi select value, call onChange
	const onChangeCallback = (value: TMultiSelectItem[]): void => {
		if (isMounted.current) {
			const newValue: string[] = value.map((item) => item.id);
			updateMultiSelectValue(newValue);
			onChange?.(value);
		}
	};

	return (
		<DSMultiSelect<TMultiSelectItem>
			clearButtonAriaLabelRes={CLEAR_RES}
			dropdownButtonAriaLabelRes={OPEN_RES}
			error={isError}
			errorMessage={errorMessage || errorText}
			invalidListItemLabelRes={INVALID_RES}
			isLoading={isLoading}
			label={label}
			listAction={canGoToDialog && (
				<Button
					childrenRes={GO_TO_DIALOG_RES}
					variant={ButtonVariants.link}
				/>
			)}
			listActionDisabled={isError}
			onListActionClick={callListActionAndCloseDropdown}
			onOpen={onOpen}
			options={options}
			value={value}
			{...otherProps}
			onChange={onChangeCallback}
		/>
	);
});

export default MultiSelect;